/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import clsx from "clsx";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import {currencyNumberFormat, translate} from "utils/utility";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {useTranslation} from "react-i18next";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import TableHeadColumn from "pages/Front/components/Table/TableHeadColumn";
import TableHeadTitle from "pages/Front/components/Table/TableHeadTitle";
import TableHead from "pages/Front/components/Table/TableHead";
import Table from "pages/Front/components/Table/Table";
import {BanknotesIcon, TagIcon} from "@heroicons/react/24/outline";
import TableBody from "pages/Front/components/Table/TableBody";
import TableColumn from "pages/Front/components/Table/TableColumn";
import TableRow from "pages/Front/components/Table/TableRow";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {IS_DEV} from "helpers/config";

interface BookingSellerRecapitulationPackageCartItemsTableProps {
    packagesCartItems: PackagesCartItem[];
    title: string;
}

const BookingSellerRecapitulationPackageCartItemsTable = (props: BookingSellerRecapitulationPackageCartItemsTableProps) => {
    const {t} = useTranslation();
    return (
        <BookingCard className={clsx("ReservationsForDate !px-0 !py-0")}>
            <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3"} title={props.title} />
            <Table className={"border-t"} responsive={true}>
                <TableHead>
                    <TableHeadColumn>
                        <TableHeadTitle>
                            <TagIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Item")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                    <TableHeadColumn>
                        <TableHeadTitle className={"justify-end"}>
                            <BanknotesIcon className={"w-4 h-4 mr-1.5"} />
                            <span>{t("Price")}</span>
                        </TableHeadTitle>
                    </TableHeadColumn>
                </TableHead>
                <TableBody>
                    {props.packagesCartItems.length > 0 &&
                        props.packagesCartItems
                            .sort((a: PackagesCartItem, b: PackagesCartItem) => {
                                if (!a.packageItem || !a.packageItem.position || !b.packageItem || !b.packageItem.position) {
                                    return 0;
                                }
                                return a.packageItem.position - b.packageItem.position;
                            })
                            .map((packagesCartItem, index) => {
                                if (!packagesCartItem.packageItem?.article_id) {
                                    return null;
                                }

                                return (
                                    <TableRow key={index}>
                                        <TableColumn className={"space-x-1.5 font-medium"}>
                                            <span>{packagesCartItem.drivers_count_adult + packagesCartItem.drivers_count_child}x</span>
                                            {packagesCartItem.packageItem?.name && <span>{translate(packagesCartItem.packageItem.name)}</span>}
                                            {packagesCartItem.packageItem.person_type === PackageItemPersonType.Child && <small>{t("children")}</small>}
                                            {IS_DEV && <span>{packagesCartItem.package_item_id}</span>}
                                            {IS_DEV &&<span>{packagesCartItem.packageItem?.article_id}</span>}
                                        </TableColumn>
                                        <TableColumn className={"text-right"}>
                                            {packagesCartItem.price && currencyNumberFormat(packagesCartItem.price)}
                                        </TableColumn>
                                    </TableRow>
                                );
                            })}
                    {props.packagesCartItems.length === 0 && (
                        <TableRow>
                            <td className={"px-3 py-2 text-center"} colSpan={2}>
                                {t("No items")}
                            </td>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </BookingCard>
    );
};

export default BookingSellerRecapitulationPackageCartItemsTable;
